import { SupabaseClient } from '@supabase/supabase-js';
import { randomUUID } from 'crypto';

// ==================== TYPES ====================

export type WalletTransactionStatus = 'RESERVED' | 'CONSUMED' | 'RELEASED';

export interface SessionWallet {
  id: string;
  userId: string;
  authorizedUserIds: string[];
  sourceType: 'EMPLOYER_MHAP' | 'PROMO' | 'PARTNERSHIP' | 'INTERNAL_OVERRIDE' | 'GIFT';
  sourceId: string | null;
  totalSessions: number;
  validFrom: string;
  validUntil: string | null;
  name: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmployerWalletParams {
  userId: string;
  orgId: string;
  programId: string;
  totalSessions: number;
  validFrom: Date;
  validUntil: Date;
  name?: string;
}

export interface WalletBalance {
  total: number;
  reserved: number;
  consumed: number;
  available: number;
}

// ==================== ENUM MAPPING ====================

/**
 * Map a wallet's `sourceType` (`SCHOOL`, `EMPLOYER_MHAP`, `PURCHASED`,
 * `GIFT`, `INTERNAL_OVERRIDE`, …) onto the `sessions.paymentSource`
 * enum (`SELF_PAY | EMPLOYER_MHAP | PROMO | PARTNERSHIP | INTERNAL`).
 *
 * The two enums drifted: `WalletSource` grew SCHOOL/PURCHASED/GIFT for
 * the imwell (school-covered) and student-topup flows, but
 * `PaymentSource` didn't add them. Inserting `paymentSource: 'SCHOOL'`
 * on the sessions row fails with an enum error, so every caller that
 * writes a session backed by a wallet must map first.
 *
 * SCHOOL collapses to EMPLOYER_MHAP because both are org-funded
 * coverage from a session-ledger perspective. Anything Alli-granted
 * (GIFT / INTERNAL_OVERRIDE / unrecognised) collapses to PROMO — the
 * closest "Alli covered this" bucket in the session enum.
 */
export function mapWalletSourceToPaymentSource(
  walletSourceType: string | null,
): 'SELF_PAY' | 'EMPLOYER_MHAP' | 'PROMO' | 'PARTNERSHIP' | 'INTERNAL' {
  switch (walletSourceType) {
    case 'SCHOOL':
    case 'EMPLOYER_MHAP':
      return 'EMPLOYER_MHAP';
    case 'PURCHASED':
      return 'SELF_PAY';
    case 'PARTNERSHIP':
      return 'PARTNERSHIP';
    case 'PROMO':
    case 'GIFT':
    case 'INTERNAL_OVERRIDE':
    default:
      return 'PROMO';
  }
}

// ==================== BALANCE CALCULATION ====================

/**
 * Calculate wallet balance from transactions (single source of truth)
 */
export async function calculateWalletBalance(
  walletId: string,
  client: SupabaseClient
): Promise<WalletBalance> {
  const { data: wallet } = await client
    .from('session_wallets')
    .select('totalSessions')
    .eq('id', walletId)
    .single();

  if (!wallet) {
    return { total: 0, reserved: 0, consumed: 0, available: 0 };
  }

  // Count RESERVED transactions
  const { count: reservedCount } = await client
    .from('session_wallet_transactions')
    .select('*', { count: 'exact', head: true })
    .eq('walletId', walletId)
    .eq('status', 'RESERVED');

  // Count CONSUMED transactions
  const { count: consumedCount } = await client
    .from('session_wallet_transactions')
    .select('*', { count: 'exact', head: true })
    .eq('walletId', walletId)
    .eq('status', 'CONSUMED');

  const reserved = reservedCount || 0;
  const consumed = consumedCount || 0;
  const available = wallet.totalSessions - reserved - consumed;

  return { total: wallet.totalSessions, reserved, consumed, available };
}

// ==================== WALLET CREATION ====================

/**
 * Create a new employer MHAP wallet for an employee
 */
export async function createEmployerWallet(
  params: CreateEmployerWalletParams,
  client: SupabaseClient
): Promise<SessionWallet | null> {
  const { userId, orgId, programId, totalSessions, validFrom, validUntil, name } = params;

  const now = new Date().toISOString();
  const { data, error } = await client
    .from('session_wallets')
    .insert({
      id: randomUUID(),
      userId,
      sourceType: 'EMPLOYER_MHAP',
      sourceId: orgId,
      totalSessions,
      validFrom: validFrom.toISOString(),
      validUntil: validUntil.toISOString(),
      name: name || null,
      notes: `Program: ${programId}`,
      authorizedUserIds: [],
      createdAt: now,
      updatedAt: now,
    })
    .select()
    .single();

  if (error) {
    console.error('[WalletService] Error creating employer wallet:', error);
    return null;
  }

  // No initial transaction - totalSessions IS the allocation
  return data;
}

/**
 * Create a wallet from a promo code
 */
export async function createPromoWallet(
  params: {
    userId: string;
    promoCodeId: string;
    promoCode: string;
    sessionsGranted: number;
    expiresAt?: Date;
  },
  client: SupabaseClient
): Promise<SessionWallet | null> {
  const { userId, promoCodeId, promoCode, sessionsGranted, expiresAt } = params;

  const validUntil = expiresAt || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  const now = new Date().toISOString();
  const { data, error } = await client
    .from('session_wallets')
    .insert({
      id: randomUUID(),
      userId,
      sourceType: 'PROMO',
      sourceId: promoCodeId,
      totalSessions: sessionsGranted,
      validFrom: now,
      validUntil: validUntil.toISOString(),
      name: `Promo: ${promoCode}`,
      authorizedUserIds: [],
      createdAt: now,
      updatedAt: now,
    })
    .select()
    .single();

  if (error) {
    console.error('[WalletService] Error creating promo wallet:', error);
    return null;
  }

  // No transaction - totalSessions IS the allocation
  return data;
}

// ==================== WALLET LOOKUP ====================

/**
 * Find an employee's wallet for a specific employer org
 * Note: We don't check validFrom here to match dashboard behavior.
 */
export async function findEmployeeWallet(
  userId: string,
  orgId: string,
  client: SupabaseClient
): Promise<SessionWallet | null> {
  const now = new Date().toISOString();

  const { data, error } = await client
    .from('session_wallets')
    .select('*')
    .eq('userId', userId)
    .eq('sourceType', 'EMPLOYER_MHAP')
    .eq('sourceId', orgId)
    .or(`validUntil.is.null,validUntil.gte.${now}`)
    .order('createdAt', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('[WalletService] Error finding employee wallet:', error);
    return null;
  }

  return data;
}

/**
 * Find best available wallet for booking (expiring soonest first)
 * Note: We don't check validFrom here to match dashboard behavior.
 * Wallets are usable as long as they haven't expired.
 */
export async function findBestAvailableWallet(
  userId: string,
  client: SupabaseClient
): Promise<{ wallet: SessionWallet; balance: WalletBalance } | null> {
  const now = new Date().toISOString();

  // Get all valid wallets (owned or authorized), sorted by expiration
  // Only check validUntil (not validFrom) to match dashboard behavior
  const { data: ownedWallets } = await client
    .from('session_wallets')
    .select('*')
    .eq('userId', userId)
    .or(`validUntil.is.null,validUntil.gte.${now}`)
    .order('validUntil', { ascending: true, nullsFirst: false });

  const { data: authorizedWallets } = await client
    .from('session_wallets')
    .select('*')
    .contains('authorizedUserIds', [userId])
    .or(`validUntil.is.null,validUntil.gte.${now}`)
    .order('validUntil', { ascending: true, nullsFirst: false });

  // Merge and sort by validUntil (expiring soonest first)
  const allWallets = [...(ownedWallets || []), ...(authorizedWallets || [])]
    .sort((a, b) => {
      if (!a.validUntil) return 1;
      if (!b.validUntil) return -1;
      return new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime();
    });

  // Find first wallet with available balance
  for (const wallet of allWallets) {
    const balance = await calculateWalletBalance(wallet.id, client);
    if (balance.available > 0) {
      return { wallet, balance };
    }
  }

  return null;
}

// ==================== SESSION RESERVATION ====================

/**
 * Reserve a session from wallet (on booking)
 */
export async function reserveWalletSession(
  walletId: string,
  sessionId: string,
  userId: string,
  client: SupabaseClient
): Promise<boolean> {
  const balance = await calculateWalletBalance(walletId, client);

  if (balance.available <= 0) {
    console.error('[WalletService] No sessions available');
    return false;
  }

  const { error } = await client
    .from('session_wallet_transactions')
    .insert({
      id: randomUUID(),
      walletId,
      status: 'RESERVED',
      sessionId,
      userId,
      reason: 'Session booked',
    });

  if (error) {
    console.error('[WalletService] Error reserving session:', error);
    return false;
  }

  return true;
}

/**
 * Update transaction status (on session completion/cancellation)
 */
export async function updateWalletTransactionStatus(
  sessionId: string,
  status: WalletTransactionStatus,
  reason: string,
  client: SupabaseClient
): Promise<boolean> {
  console.log('[WalletService] Updating transaction status:', { sessionId, status, reason });

  // First, check if a transaction exists for this session
  const { data: existingTx, error: findError } = await client
    .from('session_wallet_transactions')
    .select('id, status')
    .eq('sessionId', sessionId)
    .maybeSingle();

  if (findError) {
    console.error('[WalletService] Error finding transaction:', findError);
    return false;
  }

  if (!existingTx) {
    console.error('[WalletService] No transaction found for sessionId:', sessionId);
    return false;
  }

  console.log('[WalletService] Found existing transaction:', {
    transactionId: existingTx.id,
    currentStatus: existingTx.status,
    newStatus: status
  });

  const { error } = await client
    .from('session_wallet_transactions')
    .update({ status, reason })
    .eq('sessionId', sessionId);

  if (error) {
    console.error('[WalletService] Error updating transaction status:', error);
    return false;
  }

  console.log('[WalletService] Transaction updated successfully');
  return true;
}

// ==================== WALLET MANAGEMENT ====================

/**
 * Add an authorized user to a wallet (for shared pools)
 */
export async function addAuthorizedUser(
  walletId: string,
  userId: string,
  client: SupabaseClient
): Promise<boolean> {
  const { data: wallet, error: fetchError } = await client
    .from('session_wallets')
    .select('authorizedUserIds')
    .eq('id', walletId)
    .single();

  if (fetchError || !wallet) {
    console.error('[WalletService] Error fetching wallet for addAuthorizedUser:', fetchError);
    return false;
  }

  const currentIds = wallet.authorizedUserIds || [];

  if (currentIds.includes(userId)) {
    return true;
  }

  const { error } = await client
    .from('session_wallets')
    .update({
      authorizedUserIds: [...currentIds, userId],
      updatedAt: new Date().toISOString(),
    })
    .eq('id', walletId);

  if (error) {
    console.error('[WalletService] Error adding authorized user:', error);
    return false;
  }

  return true;
}

/**
 * Remove an authorized user from a wallet
 */
export async function removeAuthorizedUser(
  walletId: string,
  userId: string,
  client: SupabaseClient
): Promise<boolean> {
  const { data: wallet, error: fetchError } = await client
    .from('session_wallets')
    .select('authorizedUserIds')
    .eq('id', walletId)
    .single();

  if (fetchError || !wallet) {
    console.error('[WalletService] Error fetching wallet for removeAuthorizedUser:', fetchError);
    return false;
  }

  const currentIds = wallet.authorizedUserIds || [];
  const newIds = currentIds.filter((id: string) => id !== userId);

  const { error } = await client
    .from('session_wallets')
    .update({
      authorizedUserIds: newIds,
      updatedAt: new Date().toISOString(),
    })
    .eq('id', walletId);

  if (error) {
    console.error('[WalletService] Error removing authorized user:', error);
    return false;
  }

  return true;
}

/**
 * Invalidate a wallet (e.g., when employee is terminated)
 */
export async function invalidateWallet(
  userId: string,
  orgId: string,
  client: SupabaseClient
): Promise<boolean> {
  const { error } = await client
    .from('session_wallets')
    .update({
      validUntil: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    .eq('userId', userId)
    .eq('sourceType', 'EMPLOYER_MHAP')
    .eq('sourceId', orgId);

  if (error) {
    console.error('[WalletService] Error invalidating wallet:', error);
    return false;
  }

  return true;
}

// ==================== BALANCE QUERIES ====================

/**
 * Get total available sessions across all user's wallets
 * Note: We don't check validFrom here to match dashboard behavior.
 */
export async function getTotalAvailableSessions(
  userId: string,
  client: SupabaseClient
): Promise<WalletBalance> {
  const now = new Date().toISOString();

  const { data: ownedWallets } = await client
    .from('session_wallets')
    .select('id, totalSessions')
    .eq('userId', userId)
    .or(`validUntil.is.null,validUntil.gte.${now}`);

  const { data: authorizedWallets } = await client
    .from('session_wallets')
    .select('id, totalSessions')
    .contains('authorizedUserIds', [userId])
    .or(`validUntil.is.null,validUntil.gte.${now}`);

  const allWallets = [...(ownedWallets || []), ...(authorizedWallets || [])];

  let total = 0, reserved = 0, consumed = 0;

  for (const wallet of allWallets) {
    const balance = await calculateWalletBalance(wallet.id, client);
    total += balance.total;
    reserved += balance.reserved;
    consumed += balance.consumed;
  }

  return { total, reserved, consumed, available: total - reserved - consumed };
}
